import { useState, useEffect } from "react";
import { Badge, Card, MetricCard, Modal, btnPrimary, btnSecondary, btnDanger, inputStyle, labelStyle } from "./shared";

export default function Financials({
  payments, expenses, tenants, maintenance, properties, units,
  selectedYear, setSelectedYear,
  selectedPeriod,
  showAddExpense, setShowAddExpense,
  newExpense, setNewExpense, addExpense, deleteExpense,
  finTab, setFinTab,
  finPeriodFilter, setFinPeriodFilter,
  finExportTrigger,
}) {
  // When the global month picker changes year, sync the year view automatically
  const effectiveYear = selectedPeriod ? selectedPeriod.split("-")[0] : selectedYear;
  const selectedMonthNum = selectedPeriod ? parseInt(selectedPeriod.split("-")[1]) - 1 : null; // 0-based

  const [exportPeriod, setExportPeriod] = useState("annual");
  const [exportMonth, setExportMonth] = useState(() => new Date().getMonth() + 1);
  const [exportQuarter, setExportQuarter] = useState(() => Math.ceil((new Date().getMonth() + 1) / 3));
  const [exportYear, setExportYear] = useState(() => String(new Date().getFullYear()));
  const [exportPreview, setExportPreview] = useState(null);

  // Expense Log filters & sort
  const [expFilterProp,     setExpFilterProp]     = useState("");
  const [expFilterCat,      setExpFilterCat]      = useState("");
  const [expFilterDateFrom, setExpFilterDateFrom] = useState("");
  const [expFilterDateTo,   setExpFilterDateTo]   = useState("");
  const [expSortCol,        setExpSortCol]        = useState("date");
  const [expSortDir,        setExpSortDir]        = useState("desc");

  const yr = parseInt(effectiveYear);

  const resolveExpProp = (e) => {
    if (!e.property_id) return "General";
    const found = (properties || []).find(p => p.id === e.property_id);
    return found?.address || "General";
  };

  const yearPayments = payments.filter(p => p.status === "paid" && p.due?.startsWith(effectiveYear));
  const yearExpenses = expenses.filter(e => e.date?.startsWith(effectiveYear));
  const annualIncome = yearPayments.reduce((s, p) => s + (p.amount || 0), 0);
  const annualExpenseTotal = yearExpenses.reduce((s, e) => s + (e.amount || 0), 0);
  const annualNet = annualIncome - annualExpenseTotal;
  const taxEst = Math.max(0, Math.round(annualNet * 0.25));

  // By Month
  const months = Array.from({ length: 12 }, (_, i) => {
    const mo = String(i + 1).padStart(2, "0");
    const key = `${effectiveYear}-${mo}`;
    const label = new Date(yr, i, 1).toLocaleDateString("en-US", { month: "short" });
    const inc = yearPayments.filter(p => p.due?.startsWith(key)).reduce((s, p) => s + (p.amount || 0), 0);
    const exp = yearExpenses.filter(e => e.date?.startsWith(key)).reduce((s, e) => s + (e.amount || 0), 0);
    return { key, label, inc, exp, net: inc - exp };
  });
  const maxBar = Math.max(...months.map(m => Math.max(m.inc, m.exp)), 1);

  // Period filter applied by the sidebar (Q1–Q4 or YTD)
  const Q_RANGES = { Q1: [0,1,2], Q2: [3,4,5], Q3: [6,7,8], Q4: [9,10,11] };
  const currentMonthIdx = new Date().getMonth();
  const displayMonths = finPeriodFilter === "YTD"
    ? months.filter((_, i) => i <= currentMonthIdx)
    : (finPeriodFilter && Q_RANGES[finPeriodFilter])
      ? months.filter((_, i) => Q_RANGES[finPeriodFilter].includes(i))
      : months;
  const displayMaxBar    = Math.max(...displayMonths.map(m => Math.max(m.inc, m.exp)), 1);
  const displayIncome    = displayMonths.reduce((s, m) => s + m.inc, 0);
  const displayExpenses  = displayMonths.reduce((s, m) => s + m.exp, 0);
  const displayNet       = displayIncome - displayExpenses;
  const periodLabel      = finPeriodFilter ? `${finPeriodFilter} ${effectiveYear}` : effectiveYear;

  // Resolve a payment's property via unit_id -> units.property_id -> properties
  // first (the reliable relational path now that every payment is backfilled),
  // falling back to the old text-matching logic only if unit_id is missing.
  const resolveProperty = (p) => {
    if (p.unit_id) {
      const unit = (units || []).find(u => u.id === p.unit_id);
      const found = unit && (properties || []).find(pr => pr.id === unit.property_id);
      if (found) return found.address;
    }
    const str = String(p.property || "").trim();
    if (!str) {
      const t = tenants.find(t => t.name === p.tenant);
      return t?.address || t?.property || "";
    }
    const isRawId = /^\d+$/.test(str) ||
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
    if (isRawId) {
      const found = (properties || []).find(pr => String(pr.id) === str);
      if (found) return found.address;
      const t = tenants.find(t => t.name === p.tenant);
      return t?.address || t?.property || "";
    }
    return str;
  };

  // By Property
  const propIncome = {};
  const propExp = {};
  yearPayments.forEach(p => {
    const prop = resolveProperty(p) || "Unassigned";
    propIncome[prop] = (propIncome[prop] || 0) + (p.amount || 0);
  });
  yearExpenses.forEach(e => {
    const prop = resolveExpProp(e);
    propExp[prop] = (propExp[prop] || 0) + (e.amount || 0);
  });
  const allPropKeys = [...new Set([...Object.keys(propIncome), ...Object.keys(propExp)])].sort();

  // By Category
  const catMap = yearExpenses.reduce((acc, e) => {
    acc[e.category] = (acc[e.category] || 0) + (e.amount || 0);
    return acc;
  }, {});

  const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const Q_MONTHS = { 1: ["01","02","03"], 2: ["04","05","06"], 3: ["07","08","09"], 4: ["10","11","12"] };

  const csvQ = s => `"${String(s || "").replace(/"/g, '""')}"`;

  const downloadCsv = (filename, csvString) => {
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const runExport = () => {
    let title, filename, headers, rows, summaryRows, csvLines;

    if (exportPeriod === "annual") {
      const year = effectiveYear;
      if (finTab === "month") {
        title = `Leasly Financials — ${year}`;
        filename = `Leasly-Financials-${year}.csv`;
        headers = ["Month", "Income", "Expenses", "Net"];
        rows = months.map(m => [
          `${m.label} ${year}`,
          m.inc > 0 ? `$${m.inc.toLocaleString()}` : "—",
          m.exp > 0 ? `-$${m.exp.toLocaleString()}` : "—",
          `$${m.net.toLocaleString()}`,
        ]);
        csvLines = [
          "Month,Income,Expenses,Net",
          ...months.map(m => `${csvQ(m.label + " " + year)},${m.inc},${m.exp},${m.net}`),
          "",
          `"Total Income",${annualIncome},,`,
          `"Total Expenses",,${annualExpenseTotal},`,
          `"Net Profit",,,${annualNet}`,
        ];
      } else if (finTab === "property") {
        title = `Leasly Financials — By Property ${year}`;
        filename = `Leasly-Financials-ByProperty-${year}.csv`;
        headers = ["Property", "Income", "Expenses", "Net"];
        rows = allPropKeys.map(prop => {
          const inc = propIncome[prop] || 0;
          const exp = propExp[prop] || 0;
          return [prop, `$${inc.toLocaleString()}`, exp > 0 ? `-$${exp.toLocaleString()}` : "—", `$${(inc - exp).toLocaleString()}`];
        });
        csvLines = [
          "Property,Income,Expenses,Net",
          ...allPropKeys.map(prop => {
            const inc = propIncome[prop] || 0;
            const exp = propExp[prop] || 0;
            return `${csvQ(prop)},${inc},${exp},${inc - exp}`;
          }),
          "",
          `"Total Income",${annualIncome},,`,
          `"Total Expenses",,${annualExpenseTotal},`,
          `"Net Profit",,,${annualNet}`,
        ];
      } else {
        title = `Leasly Financials — By Category ${year}`;
        filename = `Leasly-Financials-ByCategory-${year}.csv`;
        headers = ["Category", "Amount"];
        rows = Object.entries(catMap).sort(([,a],[,b]) => b-a).map(([cat, amt]) => [cat, `-$${amt.toLocaleString()}`]);
        csvLines = [
          "Category,Amount",
          ...Object.entries(catMap).sort(([,a],[,b]) => b-a).map(([cat, amt]) => `${csvQ(cat)},${amt}`),
          "",
          `"Total Income",${annualIncome}`,
          `"Total Expenses",${annualExpenseTotal}`,
          `"Net Profit",${annualNet}`,
        ];
      }
      summaryRows = [
        { label: "Total Income",    value: `$${annualIncome.toLocaleString()}`,        color: "#22c55e" },
        { label: "Total Expenses",  value: `-$${annualExpenseTotal.toLocaleString()}`, color: "#ef4444" },
        { label: "Net Profit",      value: `$${annualNet.toLocaleString()}`,           color: annualNet >= 0 ? "#163C2F" : "#ef4444" },
      ];
    } else {
      // Monthly or Quarterly — transaction-level export
      let filteredPayments, filteredExpenses;
      const year = exportYear;

      if (exportPeriod === "monthly") {
        const mo = String(exportMonth).padStart(2, "0");
        const key = `${year}-${mo}`;
        filteredPayments = payments.filter(p => p.due?.startsWith(key) && p.status === "paid");
        filteredExpenses = expenses.filter(e => e.date?.startsWith(key));
        title = `Leasly Financials — ${MONTH_NAMES[exportMonth - 1]} ${year}`;
        filename = `Leasly-Financials-${MONTH_NAMES[exportMonth - 1]}-${year}.csv`;
      } else {
        const qMs = Q_MONTHS[exportQuarter];
        filteredPayments = payments.filter(p => p.due && p.status === "paid" && qMs.some(m => p.due.startsWith(`${year}-${m}`)));
        filteredExpenses = expenses.filter(e => e.date && qMs.some(m => e.date.startsWith(`${year}-${m}`)));
        title = `Leasly Financials — Q${exportQuarter} ${year}`;
        filename = `Leasly-Financials-Q${exportQuarter}-${year}.csv`;
      }

      const totalIncome   = filteredPayments.reduce((s, p) => s + (p.amount || 0), 0);
      const totalExpenses = filteredExpenses.reduce((s, e) => s + (e.amount || 0), 0);
      const netProfit     = totalIncome - totalExpenses;

      headers = ["Date", "Type", "Category", "Property", "Description", "Amount"];

      const sortedPayments = [...filteredPayments].sort((a, b) => (a.paid || "").localeCompare(b.paid || ""));
      const sortedExpenses = [...filteredExpenses].sort((a, b) => (a.date || "").localeCompare(b.date || ""));

      const incomeDisplayRows = sortedPayments.map(p => {
        const prop = resolveProperty(p);
        return [p.paid || "", "Income", "Rent", prop, `Rent — ${p.tenant}`, `$${(p.amount || 0).toLocaleString()}`];
      });
      const expenseDisplayRows = sortedExpenses.map(e => [
        e.date || "", "Expense", e.category || "", resolveExpProp(e), e.description || "", `-$${(e.amount || 0).toLocaleString()}`,
      ]);

      rows = [...incomeDisplayRows, ...expenseDisplayRows];

      const incomeCsvRows = sortedPayments.map(p => {
        const prop = resolveProperty(p);
        return [p.paid || "", "Income", "Rent", csvQ(prop), csvQ(`Rent — ${p.tenant}`), p.amount ?? ""].join(",");
      });
      const expenseCsvRows = sortedExpenses.map(e => [
        e.date || "", "Expense", csvQ(e.category || ""), csvQ(resolveExpProp(e)), csvQ(e.description || ""), e.amount ?? "",
      ].join(","));

      summaryRows = [
        { label: "Total Income",   value: `$${totalIncome.toLocaleString()}`,   color: "#22c55e" },
        { label: "Total Expenses", value: `-$${totalExpenses.toLocaleString()}`, color: "#ef4444" },
        { label: "Net Profit",     value: `$${netProfit.toLocaleString()}`,      color: netProfit >= 0 ? "#163C2F" : "#ef4444" },
      ];

      csvLines = [
        "Date,Type,Category,Property,Description,Amount",
        ...incomeCsvRows,
        ...expenseCsvRows,
        "",
        `"","","","","Total Income",${totalIncome}`,
        `"","","","","Total Expenses",${totalExpenses}`,
        `"","","","","Net Profit",${netProfit}`,
      ];
    }

    setExportPreview({ title, filename, headers, rows, summaryRows, csvString: csvLines.join("\n") });
  };

  // Triggered by the sidebar "Export CSV" button
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (finExportTrigger > 0) runExport(); }, [finExportTrigger]);

  // ── Expense Log: filter + sort ────────────────────────────────────────────
  const EXP_CATS = ["Insurance","Property Taxes","Repairs & Maintenance","Utilities","Landscaping","Management Fees","Other"];
  const expPropOptions = [...new Set((properties || []).map(p => p.address))].filter(Boolean).sort();

  const filteredExpenses = yearExpenses
    .filter(e => {
      if (expFilterProp && resolveExpProp(e) !== expFilterProp) return false;
      if (expFilterCat  && e.category !== expFilterCat)         return false;
      if (expFilterDateFrom && (e.date || "") < expFilterDateFrom) return false;
      if (expFilterDateTo   && (e.date || "") > expFilterDateTo)   return false;
      return true;
    })
    .sort((a, b) => {
      let cmp = 0;
      if (expSortCol === "date")     cmp = (a.date || "").localeCompare(b.date || "");
      if (expSortCol === "amount")   cmp = (a.amount || 0) - (b.amount || 0);
      if (expSortCol === "category") cmp = (a.category || "").localeCompare(b.category || "");
      if (expSortCol === "property") cmp = resolveExpProp(a).localeCompare(resolveExpProp(b));
      return expSortDir === "asc" ? cmp : -cmp;
    });
  const filteredExpTotal = filteredExpenses.reduce((s, e) => s + (e.amount || 0), 0);
  const hasExpFilters = !!(expFilterProp || expFilterCat || expFilterDateFrom || expFilterDateTo);

  const clearExpFilters = () => {
    setExpFilterProp(""); setExpFilterCat("");
    setExpFilterDateFrom(""); setExpFilterDateTo("");
    setExpSortCol("date"); setExpSortDir("desc");
  };
  const handleExpSort = (col) => {
    if (expSortCol === col) {
      setExpSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setExpSortCol(col);
      setExpSortDir(col === "date" || col === "amount" ? "desc" : "asc");
    }
  };

  const thStyle = { padding: "10px 14px", textAlign: "left", fontSize: 10, color: "#9B9690", fontFamily: "'Manrope', sans-serif", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 };
  const tdStyle = { padding: "12px 14px", fontSize: 13, fontFamily: "'Manrope', sans-serif" };

  // ── Summary cards — follow the Annual/Quarterly/Monthly toggle above ───────
  let summaryPayments, summaryExpenses, summaryLabelPrefix;
  if (exportPeriod === "monthly") {
    const mo = String(exportMonth).padStart(2, "0");
    const key = `${exportYear}-${mo}`;
    summaryPayments = payments.filter(p => p.status === "paid" && p.due?.startsWith(key));
    summaryExpenses = expenses.filter(e => e.date?.startsWith(key));
    summaryLabelPrefix = "Monthly";
  } else if (exportPeriod === "quarterly") {
    const qMs = Q_MONTHS[exportQuarter];
    summaryPayments = payments.filter(p => p.status === "paid" && p.due && qMs.some(m => p.due.startsWith(`${exportYear}-${m}`)));
    summaryExpenses = expenses.filter(e => e.date && qMs.some(m => e.date.startsWith(`${exportYear}-${m}`)));
    summaryLabelPrefix = "Quarterly";
  } else {
    summaryPayments = yearPayments;
    summaryExpenses = yearExpenses;
    summaryLabelPrefix = "Annual";
  }
  const summaryIncome = summaryPayments.reduce((s, p) => s + (p.amount || 0), 0);
  const summaryExpenseTotal = summaryExpenses.reduce((s, e) => s + (e.amount || 0), 0);
  const summaryNet = summaryIncome - summaryExpenseTotal;
  const summaryTaxEst = Math.max(0, Math.round(summaryNet * 0.25));

  return (
    <>
      {/* Header */}
      <div className="app-page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
        <div>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, fontSize: 32, color: "#2B2B2B", margin: 0 }}>Financials</h1>
          <div style={{ color: "#9B9690", marginTop: 4, fontSize: 14 }}>{periodLabel} Summary</div>
        </div>
        <div className="app-fin-controls" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="number" min="2020" max="2035"
            value={effectiveYear}
            onChange={e => { setSelectedYear(e.target.value); setFinPeriodFilter(null); }}
            style={{ ...inputStyle, width: 90, padding: "7px 12px" }}
          />
          {/* Export controls */}
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", background: "#F5F1EA", borderRadius: 10, padding: "6px 10px", border: "1px solid #e0dbd0" }}>
            <div style={{ display: "flex", background: "#FFFFFF", borderRadius: 6, padding: 2, border: "1px solid #e0dbd0" }}>
              {[["annual","Annual"],["quarterly","Quarterly"],["monthly","Monthly"]].map(([v,l]) => (
                <button key={v} onClick={() => setExportPeriod(v)} style={{ padding: "4px 10px", borderRadius: 5, border: "none", fontSize: 11, background: exportPeriod === v ? "#163C2F" : "transparent", color: exportPeriod === v ? "#fff" : "#9B9690", cursor: "pointer", fontFamily: "inherit", fontWeight: 600, whiteSpace: "nowrap" }}>{l}</button>
              ))}
            </div>
            {exportPeriod === "quarterly" && (
              <div style={{ display: "flex", gap: 3 }}>
                {[1,2,3,4].map(q => (
                  <button key={q} onClick={() => setExportQuarter(q)} style={{ padding: "4px 9px", borderRadius: 5, border: `1.5px solid ${exportQuarter === q ? "#163C2F" : "#d4cfc4"}`, background: exportQuarter === q ? "#163C2F" : "#fff", color: exportQuarter === q ? "#fff" : "#9B9690", cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 700 }}>Q{q}</button>
                ))}
              </div>
            )}
            {exportPeriod === "monthly" && (
              <select value={exportMonth} onChange={e => setExportMonth(Number(e.target.value))} style={{ ...inputStyle, width: "auto", padding: "4px 8px", fontSize: 11, height: "auto" }}>
                {MONTH_NAMES.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
              </select>
            )}
            {exportPeriod !== "annual" && (
              <input type="number" min="2020" max="2035" value={exportYear} onChange={e => setExportYear(e.target.value)} style={{ ...inputStyle, width: 72, padding: "4px 8px", fontSize: 11, height: "auto" }} />
            )}
            <button style={{ ...btnSecondary, padding: "5px 12px", fontSize: 12, whiteSpace: "nowrap" }} onClick={runExport}>↓ Preview &amp; Export</button>
          </div>
          <button style={btnPrimary} onClick={() => setShowAddExpense(true)}>+ Add Expense</button>
        </div>
      </div>

      {/* Summary Stat Cards — reflect the Annual/Quarterly/Monthly toggle */}
      <div className="app-metric-row" style={{ display: "flex", gap: 16, marginBottom: 28, flexWrap: "wrap" }}>
        <MetricCard label={`${summaryLabelPrefix} Income`} value={`$${summaryIncome.toLocaleString()}`} sub={`${summaryPayments.length} payments`} accent="#22c55e" />
        <MetricCard label={`${summaryLabelPrefix} Expenses`} value={`$${summaryExpenseTotal.toLocaleString()}`} sub={`${summaryExpenses.length} entries`} accent="#ef4444" />
        <MetricCard
          label="Net Profit"
          value={<span style={{ color: summaryNet >= 0 ? "#22c55e" : "#ef4444" }}>${summaryNet.toLocaleString()}</span>}
          sub="Income minus expenses"
          accent={summaryNet >= 0 ? "#22c55e" : "#ef4444"}
        />
        <MetricCard label="Est. Tax (25%)" value={`$${summaryTaxEst.toLocaleString()}`} sub="Rough estimate only" accent="#a78bfa" />
      </div>

      {/* Tab Bar */}
      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid #e0dbd0" }}>
        {[["month","📅 By Month"],["property","🏠 By Property"],["category","📂 By Category"],["tax","🧾 Tax Summary"]].map(([tab, label]) => (
          <button key={tab} onClick={() => setFinTab(tab)} style={{
            border: "none", background: "none", padding: "10px 18px", cursor: "pointer",
            fontWeight: finTab === tab ? 700 : 500, fontSize: 14,
            color: finTab === tab ? "#163C2F" : "#9B9690",
            borderBottom: finTab === tab ? "2px solid #163C2F" : "2px solid transparent",
            fontFamily: "'Manrope', sans-serif", marginBottom: -1,
          }}>{label}</button>
        ))}
      </div>

      {/* ── By Month ── */}
      {finTab === "month" && (
        <>
          <Card style={{ marginBottom: 20 }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 800, fontSize: 16, marginBottom: 20 }}>Income vs Expenses — {periodLabel}</div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 140, marginBottom: 12 }}>
              {displayMonths.map((m, i) => (
                <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                  <div style={{ display: "flex", gap: 3, alignItems: "flex-end", height: 110 }}>
                    <div title={`Income: $${m.inc.toLocaleString()}`} style={{ width: 14, background: "#7FB98E", borderRadius: "3px 3px 0 0", height: `${Math.round((m.inc / displayMaxBar) * 110)}px`, minHeight: m.inc > 0 ? 3 : 0 }} />
                    <div title={`Expenses: $${m.exp.toLocaleString()}`} style={{ width: 14, background: "#B08D57", borderRadius: "3px 3px 0 0", height: `${Math.round((m.exp / displayMaxBar) * 110)}px`, minHeight: m.exp > 0 ? 3 : 0 }} />
                  </div>
                  <div style={{ fontSize: 10, color: "#9B9690", fontFamily: "'Manrope', sans-serif" }}>{m.label}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 12, height: 12, background: "#7FB98E", borderRadius: 2 }} /><span style={{ fontSize: 11, color: "#9B9690" }}>Income</span></div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 12, height: 12, background: "#B08D57", borderRadius: 2 }} /><span style={{ fontSize: 11, color: "#9B9690" }}>Expenses</span></div>
            </div>
          </Card>

          <Card>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 800, fontSize: 16, marginBottom: 16 }}>Monthly Breakdown — {periodLabel}</div>
            <div className="app-table-scroll">
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #e0dbd0" }}>
                    {["Month", "Income", "Expenses", "Net"].map(h => <th key={h} style={thStyle}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {displayMonths.map((m, i) => {
                    const globalIdx = months.indexOf(m);
                    return (
                      <tr key={m.key} style={{ borderBottom: "1px solid #F0EDE6", background: selectedMonthNum !== null && globalIdx === selectedMonthNum ? "#EAF2ED" : undefined }}>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{m.label} {effectiveYear}</td>
                        <td style={{ ...tdStyle, color: m.inc > 0 ? "#22c55e" : "#9B9690", fontWeight: m.inc > 0 ? 700 : 400 }}>{m.inc > 0 ? `$${m.inc.toLocaleString()}` : "—"}</td>
                        <td style={{ ...tdStyle, color: m.exp > 0 ? "#ef4444" : "#9B9690" }}>{m.exp > 0 ? `-$${m.exp.toLocaleString()}` : "—"}</td>
                        <td style={{ ...tdStyle, fontWeight: 700, color: m.net >= 0 ? "#163C2F" : "#ef4444" }}>{(m.inc > 0 || m.exp > 0) ? `$${m.net.toLocaleString()}` : "—"}</td>
                      </tr>
                    );
                  })}
                  <tr style={{ borderTop: "2px solid #e0dbd0", background: "#F5F1EA" }}>
                    <td style={{ ...tdStyle, fontWeight: 700 }}>Total {periodLabel}</td>
                    <td style={{ ...tdStyle, color: "#22c55e", fontWeight: 700 }}>${displayIncome.toLocaleString()}</td>
                    <td style={{ ...tdStyle, color: "#ef4444", fontWeight: 700 }}>-${displayExpenses.toLocaleString()}</td>
                    <td style={{ ...tdStyle, fontWeight: 700, color: displayNet >= 0 ? "#163C2F" : "#ef4444" }}>${displayNet.toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* ── By Property ── */}
      {finTab === "property" && (
        <Card>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 800, fontSize: 16, marginBottom: 16 }}>Per Property P&L — {effectiveYear}</div>
          {allPropKeys.length === 0
            ? <div style={{ color: "#9B9690", fontSize: 13 }}>No payment or expense data for {effectiveYear}.</div>
            : <div className="app-table-scroll">
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #e0dbd0" }}>
                      {["Property", "Income", "Expenses", "Net"].map(h => <th key={h} style={thStyle}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {allPropKeys.map(prop => {
                      const inc = propIncome[prop] || 0;
                      const exp = propExp[prop] || 0;
                      const net = inc - exp;
                      return (
                        <tr key={prop} style={{ borderBottom: "1px solid #F0EDE6" }}>
                          <td style={{ ...tdStyle, fontWeight: 600 }}>{prop}</td>
                          <td style={{ ...tdStyle, color: "#22c55e", fontWeight: 700 }}>${inc.toLocaleString()}</td>
                          <td style={{ ...tdStyle, color: exp > 0 ? "#ef4444" : "#9B9690" }}>{exp > 0 ? `-$${exp.toLocaleString()}` : "—"}</td>
                          <td style={{ ...tdStyle, fontWeight: 700, color: net >= 0 ? "#163C2F" : "#ef4444" }}>${net.toLocaleString()}</td>
                        </tr>
                      );
                    })}
                    <tr style={{ borderTop: "2px solid #e0dbd0", background: "#F5F1EA" }}>
                      <td style={{ ...tdStyle, fontWeight: 700 }}>All Properties</td>
                      <td style={{ ...tdStyle, color: "#22c55e", fontWeight: 700 }}>${annualIncome.toLocaleString()}</td>
                      <td style={{ ...tdStyle, color: "#ef4444", fontWeight: 700 }}>-${annualExpenseTotal.toLocaleString()}</td>
                      <td style={{ ...tdStyle, fontWeight: 700, color: annualNet >= 0 ? "#163C2F" : "#ef4444" }}>${annualNet.toLocaleString()}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
          }
        </Card>
      )}

      {/* ── By Category ── */}
      {finTab === "category" && (
        <Card>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 800, fontSize: 16, marginBottom: 16 }}>Expense Breakdown by Category — {effectiveYear}</div>
          {Object.keys(catMap).length === 0
            ? <div style={{ color: "#9B9690", fontSize: 13 }}>No expenses recorded for {effectiveYear}.</div>
            : Object.entries(catMap).sort(([,a],[,b]) => b-a).map(([cat, amt]) => {
                const pct = annualExpenseTotal > 0 ? Math.round((amt / annualExpenseTotal) * 100) : 0;
                return (
                  <div key={cat} style={{ marginBottom: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{cat}</span>
                      <span style={{ fontSize: 13, fontFamily: "'Manrope', sans-serif", color: "#ef4444" }}>
                        -${amt.toLocaleString()} <span style={{ color: "#9B9690" }}>({pct}%)</span>
                      </span>
                    </div>
                    <div style={{ background: "#E8E4DC", borderRadius: 4, height: 8 }}>
                      <div style={{ background: "#B08D57", borderRadius: 4, height: 8, width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })
          }
        </Card>
      )}

      {/* ── Tax Summary ── */}
      {finTab === "tax" && (
        <Card style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 800, fontSize: 16, marginBottom: 20 }}>Tax Summary — {effectiveYear}</div>

          {/* Est. tax callout */}
          <div style={{ background: "#F5F1EA", border: "1px solid #e0dbd0", borderRadius: 12, padding: "18px 20px", marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 12, color: "#9B9690", fontFamily: "'Manrope', sans-serif", marginBottom: 4 }}>Estimated Tax Liability (25%)</div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 36, fontWeight: 800, color: "#163C2F", lineHeight: 1 }}>${taxEst.toLocaleString()}</div>
              <div style={{ fontSize: 11, color: "#9B9690", marginTop: 6 }}>Based on net profit of ${annualNet.toLocaleString()}</div>
            </div>
            <div style={{ fontSize: 32 }}>🧾</div>
          </div>

          {/* Income / Expenses / Net rows */}
          {[
            { label: "Gross Rental Income",        value: `$${annualIncome.toLocaleString()}`,         color: "#22c55e" },
            { label: "Total Deductible Expenses",   value: `-$${annualExpenseTotal.toLocaleString()}`,  color: "#ef4444" },
            { label: "Net Taxable Income",          value: `$${annualNet.toLocaleString()}`,            color: annualNet >= 0 ? "#163C2F" : "#ef4444" },
          ].map(r => (
            <div key={r.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "10px 0", borderBottom: "1px solid #F0EDE6" }}>
              <span style={{ fontSize: 13, fontFamily: "'Manrope', sans-serif" }}>{r.label}</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: r.color }}>{r.value}</span>
            </div>
          ))}

          {/* Expense breakdown by category */}
          {Object.keys(catMap).length > 0 && (
            <>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 800, fontSize: 14, marginTop: 20, marginBottom: 10 }}>Deductible Expense Breakdown</div>
              {Object.entries(catMap).sort(([,a],[,b]) => b - a).map(([cat, amt]) => (
                <div key={cat} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "8px 0", borderBottom: "1px solid #F0EDE6" }}>
                  <span style={{ fontSize: 13, fontFamily: "'Manrope', sans-serif" }}>{cat}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#ef4444" }}>-${amt.toLocaleString()}</span>
                </div>
              ))}
            </>
          )}

          <div style={{ marginTop: 20, fontSize: 11, color: "#9B9690", fontStyle: "italic", background: "#F5F1EA", borderRadius: 8, padding: "10px 14px", lineHeight: 1.6 }}>
            This is a rough estimate for planning purposes only. The 25% rate is an approximation — actual tax liability depends on your filing status, deductions, and other income. Consult a qualified tax professional for accurate advice.
          </div>
        </Card>
      )}

      {/* ── Expense Log ── */}
      <Card style={{ marginTop: 20 }}>
        {/* Header row */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 800, fontSize: 16 }}>Expense Log — {effectiveYear}</div>
          {hasExpFilters && (
            <button onClick={clearExpFilters} style={{ fontSize: 11, color: "#9B9690", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", textDecoration: "underline" }}>
              Clear Filters
            </button>
          )}
        </div>

        {/* Filter bar */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12, padding: "10px 12px", background: "#F5F1EA", borderRadius: 8, border: "1px solid #E8E4DC" }}>
          <select
            value={expFilterProp}
            onChange={e => setExpFilterProp(e.target.value)}
            style={{ ...inputStyle, fontSize: 12, padding: "5px 10px", height: "auto", minWidth: 150 }}
          >
            <option value="">All Properties</option>
            {expPropOptions.map(p => <option key={p} value={p}>{p}</option>)}
          </select>

          <select
            value={expFilterCat}
            onChange={e => setExpFilterCat(e.target.value)}
            style={{ ...inputStyle, fontSize: 12, padding: "5px 10px", height: "auto", minWidth: 160 }}
          >
            <option value="">All Categories</option>
            {EXP_CATS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ fontSize: 11, color: "#9B9690", whiteSpace: "nowrap" }}>From</span>
            <input
              type="date"
              value={expFilterDateFrom}
              onChange={e => setExpFilterDateFrom(e.target.value)}
              style={{ ...inputStyle, fontSize: 12, padding: "5px 10px", height: "auto", width: 140 }}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ fontSize: 11, color: "#9B9690", whiteSpace: "nowrap" }}>To</span>
            <input
              type="date"
              value={expFilterDateTo}
              onChange={e => setExpFilterDateTo(e.target.value)}
              style={{ ...inputStyle, fontSize: 12, padding: "5px 10px", height: "auto", width: 140 }}
            />
          </div>

          {hasExpFilters && (
            <button
              onClick={clearExpFilters}
              style={{ padding: "5px 12px", borderRadius: 6, border: "1px solid #E8E4DC", background: "#fff", color: "#9B9690", fontSize: 12, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, whiteSpace: "nowrap" }}
            >
              ✕ Clear
            </button>
          )}
        </div>

        {/* Summary */}
        <div style={{ fontSize: 12, color: "#9B9690", marginBottom: 12, fontFamily: "'Manrope', sans-serif" }}>
          Showing{" "}
          <strong style={{ color: "#2B2B2B" }}>{filteredExpenses.length}</strong>
          {" "}expense{filteredExpenses.length !== 1 ? "s" : ""}
          {" — "}
          <strong style={{ color: "#ef4444" }}>-${filteredExpTotal.toLocaleString()}</strong>
          {" "}total
        </div>

        {yearExpenses.length === 0
          ? <div style={{ color: "#9B9690", fontSize: 13 }}>No expenses recorded for {effectiveYear}.</div>
          : filteredExpenses.length === 0
            ? <div style={{ color: "#9B9690", fontSize: 13 }}>No expenses match the current filters.</div>
            : <div className="app-table-scroll">
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #e0dbd0" }}>
                      {[
                        { key: "date",     label: "Date"        },
                        { key: "category", label: "Category"    },
                        { key: null,       label: "Description" },
                        { key: "property", label: "Property"    },
                        { key: "amount",   label: "Amount"      },
                        { key: null,       label: "Receipt"     },
                        { key: null,       label: ""            },
                      ].map(({ key, label }, i) => (
                        <th
                          key={i}
                          style={{ ...thStyle, cursor: key ? "pointer" : "default", userSelect: "none" }}
                          onClick={() => key && handleExpSort(key)}
                        >
                          {label}
                          {key && (
                            <span style={{ marginLeft: 4, fontSize: 9, opacity: expSortCol === key ? 1 : 0.3 }}>
                              {expSortCol === key ? (expSortDir === "asc" ? "↑" : "↓") : "↕"}
                            </span>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredExpenses.map((e, idx) => (
                      <tr key={idx} style={{ borderBottom: "1px solid #F0EDE6" }}>
                        <td style={{ ...tdStyle, color: "#9B9690" }}>{e.date}</td>
                        <td style={{ ...tdStyle }}><Badge label={e.category} color="#3b82f6" /></td>
                        <td style={{ ...tdStyle, fontWeight: 500 }}>{e.description}</td>
                        <td style={{ ...tdStyle, color: "#9B9690" }}>{resolveExpProp(e)}</td>
                        <td style={{ ...tdStyle, color: "#ef4444", fontWeight: 700 }}>-${e.amount?.toLocaleString()}</td>
                        <td style={{ ...tdStyle }}>
                          {e.receipt_url
                            ? <a href={e.receipt_url} target="_blank" rel="noreferrer" style={{ color: "#214D3A", fontSize: 12, fontWeight: 600 }}>📎 View</a>
                            : <span style={{ color: "#9B9690", fontSize: 12 }}>—</span>}
                        </td>
                        <td style={{ ...tdStyle }}>
                          <button onClick={() => deleteExpense(e.id)} style={{ ...btnDanger, padding: "3px 7px" }}>🗑</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
        }
      </Card>

      {/* ── Export Preview Modal ── */}
      {exportPreview && (
        <div style={{ position: "fixed", inset: 0, background: "#00000099", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="app-modal-inner" style={{ background: "#FFFFFF", border: "1px solid #d4cfc4", borderRadius: 16, padding: 28, width: 760, maxWidth: "96vw", maxHeight: "92vh", overflowY: "auto", display: "flex", flexDirection: "column" }}>
            {/* Modal header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexShrink: 0 }}>
              <div>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 800, fontSize: 20, color: "#163C2F" }}>{exportPreview.title}</div>
                <div style={{ fontSize: 12, color: "#9B9690", marginTop: 3 }}>Review before downloading · {exportPreview.rows.length} row{exportPreview.rows.length !== 1 ? "s" : ""}</div>
              </div>
              <button onClick={() => setExportPreview(null)} style={{ ...btnSecondary, padding: "4px 10px", flexShrink: 0 }}>✕</button>
            </div>

            {/* Preview table */}
            <div style={{ overflowX: "auto", flex: 1, marginBottom: 20 }}>
              <table id="fin-export-preview-table" style={{ width: "100%", borderCollapse: "collapse", minWidth: 480 }}>
                <thead>
                  <tr>
                    {exportPreview.headers.map(h => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: "left", background: "#163C2F", color: "#F5F1EA", fontFamily: "'Manrope', sans-serif", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {exportPreview.rows.map((row, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? "#FFFFFF" : "#F5F1EA" }}>
                      {row.map((cell, j) => (
                        <td key={j} style={{ padding: "9px 14px", fontSize: 13, fontFamily: "'Manrope', sans-serif", borderBottom: "1px solid #F0EDE6", whiteSpace: j === row.length - 1 ? "nowrap" : "normal" }}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                  {/* Summary rows */}
                  {exportPreview.summaryRows.map((s, i) => (
                    <tr key={`sum-${i}`} style={{ background: "#F5F1EA", borderTop: i === 0 ? "2px solid #163C2F" : "none" }}>
                      <td
                        colSpan={exportPreview.headers.length - 1}
                        style={{ padding: "10px 14px", fontWeight: 700, fontSize: 13, fontFamily: "'Manrope', sans-serif", color: "#163C2F" }}
                      >{s.label}</td>
                      <td style={{ padding: "10px 14px", fontWeight: 700, fontSize: 13, fontFamily: "'Manrope', sans-serif", color: s.color, textAlign: "right", whiteSpace: "nowrap" }}>{s.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexShrink: 0 }}>
              <button onClick={() => setExportPreview(null)} style={btnSecondary}>✕ Close</button>
              <button
                onClick={() => {
                  const table = document.getElementById("fin-export-preview-table");
                  if (!table) return;
                  const win = window.open("", "_blank");
                  win.document.write(`<!DOCTYPE html><html><head><title>${exportPreview.title}</title><style>
                    body{font-family:Georgia,serif;padding:32px;color:#163C2F;}
                    h2{font-family:Georgia,serif;color:#163C2F;margin-bottom:4px;}
                    p{font-size:12px;color:#9B9690;margin:0 0 20px;}
                    table{width:100%;border-collapse:collapse;}
                    thead th{background:#163C2F;color:#F5F1EA;padding:10px 14px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.07em;font-weight:600;}
                    tbody tr:nth-child(even){background:#F5F1EA;}
                    tbody td{padding:9px 14px;font-size:13px;border-bottom:1px solid #e0dbd0;}
                    .summary-row td{font-weight:700;border-top:2px solid #163C2F;}
                  </style></head><body>
                    <h2>${exportPreview.title}</h2>
                    <p>Generated by Leasly · ${new Date().toLocaleDateString()}</p>
                    ${table.outerHTML}
                  </body></html>`);
                  win.document.close();
                  setTimeout(() => win.print(), 400);
                }}
                style={btnSecondary}
              >🖨️ Print</button>
              <button onClick={() => downloadCsv(exportPreview.filename, exportPreview.csvString)} style={btnPrimary}>↓ Download CSV</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Expense Modal */}
      {showAddExpense && (
        <Modal title="Add Expense" onClose={() => setShowAddExpense(false)} onSave={addExpense}>
          <div>
            <label style={labelStyle}>Date</label>
            <input type="date" value={newExpense.date} onChange={e => setNewExpense(s => ({ ...s, date: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Property</label>
            <select value={newExpense.property_id || ""} onChange={e => setNewExpense(s => ({ ...s, property_id: e.target.value || null }))} style={inputStyle}>
              <option value="">— None / General —</option>
              {(properties || []).map(p => <option key={p.id} value={p.id}>{p.address}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Category</label>
            <select value={newExpense.category} onChange={e => setNewExpense(s => ({ ...s, category: e.target.value }))} style={inputStyle}>
              {["Insurance","Property Taxes","Repairs & Maintenance","Utilities","Landscaping","Management Fees","Other"].map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Description</label>
            <input type="text" value={newExpense.description} onChange={e => setNewExpense(s => ({ ...s, description: e.target.value }))} style={inputStyle} placeholder="e.g. State Farm policy renewal" />
          </div>
          <div>
            <label style={labelStyle}>Amount ($)</label>
            <input type="number" value={newExpense.amount} onChange={e => setNewExpense(s => ({ ...s, amount: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Receipt / Invoice (optional)</label>
            <input type="file" accept="image/*,.pdf" onChange={e => setNewExpense(s => ({ ...s, receiptFile: e.target.files[0] || null }))} style={{ ...inputStyle, padding: "7px 13px", cursor: "pointer" }} />
            {newExpense.receiptFile && <div style={{ fontSize: 12, color: "#22c55e", marginTop: 6 }}>✅ {newExpense.receiptFile.name}</div>}
          </div>
        </Modal>
      )}
    </>
  );
}
